import ButtonGroupCustomized from '@/components/ButtonGroupCustomized'
import ButtonGroupSkeleton from '@/components/skeletons/ButtonGroupCustomized'
import EventSkeleton from '@/components/skeletons/EventSkeleton'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import { getEventPopularByDate } from '@/modules/Events/components/services'
import { EmployeeEvent } from '@/modules/Events/components/types'
import { formatDateWithFormatter } from '@/utils/function'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import NoEvents from '../NoEvents'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface CommunityProps {
    date?: string
}

interface EventGroup {
    startTime: string
    endTime: string
    events: EmployeeEvent[]
}

const Community: React.FC<CommunityProps> = ({
    date = formatDateWithFormatter(dayjs(new Date()), 'YYYY-MM-DD'),
}) => {
    const [eventScope, setEventScope] = useState<'public' | 'personal'>(
        'public'
    )
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const groupRefs = useRef<(HTMLDivElement | null)[]>([])

    const { t } = useTranslation('dashboard')

    const { data: eventsData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EVENTS_POPULAR, date],
        queryFn: () => getEventPopularByDate(date),
    })

    // Helper function to convert time string to minutes
    const timeToMinutes = (timeStr: string): number => {
        const [hours, minutes] = timeStr.split(':').map(Number)
        return hours * 60 + (minutes || 0)
    }

    // Helper function to get current time in minutes
    const getCurrentTimeInMinutes = (): number => {
        const now = new Date()
        return now.getHours() * 60 + now.getMinutes()
    }

    // Helper function to check if current time is within a time range
    const isCurrentTimeInRange = (
        startTime: string,
        endTime: string
    ): boolean => {
        const currentMin = getCurrentTimeInMinutes()
        const startMin = timeToMinutes(startTime)
        const endMin = timeToMinutes(endTime)
        return currentMin >= startMin && currentMin <= endMin
    }

    // Helper function to check if two time ranges overlap or are close
    const isTimeOverlapOrClose = (
        start1: string,
        end1: string,
        start2: string,
        end2: string,
        gapThreshold: number = 30 // minutes
    ): boolean => {
        const start1Min = timeToMinutes(start1)
        const end1Min = timeToMinutes(end1)
        const start2Min = timeToMinutes(start2)
        const end2Min = timeToMinutes(end2)

        // Check overlap
        const overlap = start1Min < end2Min && start2Min < end1Min

        // Check if gap between events is small
        const gap = Math.min(
            Math.abs(start2Min - end1Min),
            Math.abs(start1Min - end2Min)
        )
        const closeInTime = gap <= gapThreshold

        return overlap || closeInTime
    }

    // Helper function to find the closest group to current time
    const findClosestGroupToCurrentTime = (groups: EventGroup[]): number => {
        const currentMin = getCurrentTimeInMinutes()
        let closestIndex = 0
        let minDistance = Infinity

        groups.forEach((group, index) => {
            const groupStartMin = timeToMinutes(group.startTime)
            const groupEndMin = timeToMinutes(group.endTime)

            // If current time is within this group, return it immediately
            if (currentMin >= groupStartMin && currentMin <= groupEndMin) {
                closestIndex = index
                minDistance = 0
                return
            }

            // Calculate distance to this group
            let distance
            if (currentMin < groupStartMin) {
                distance = groupStartMin - currentMin
            } else {
                distance = currentMin - groupEndMin
            }

            if (distance < minDistance) {
                minDistance = distance
                closestIndex = index
            }
        })

        return closestIndex
    }

    // Group events by time overlap/proximity
    const groupEventsByTime = (events: EmployeeEvent[]): EventGroup[] => {
        if (!events || events.length === 0) return []

        // Sort events by start time
        const sortedEvents = [...events].sort(
            (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
        )

        const groups: EventGroup[] = []

        for (const event of sortedEvents) {
            let addedToGroup = false

            // Try to add to existing group
            for (const group of groups) {
                if (
                    isTimeOverlapOrClose(
                        group.startTime,
                        group.endTime,
                        event.start_time,
                        event.end_time
                    )
                ) {
                    group.events.push(event)
                    // Update group time range
                    const groupStartMin = timeToMinutes(group.startTime)
                    const groupEndMin = timeToMinutes(group.endTime)
                    const eventStartMin = timeToMinutes(event.start_time)
                    const eventEndMin = timeToMinutes(event.end_time)

                    const newStartMin = Math.min(groupStartMin, eventStartMin)
                    const newEndMin = Math.max(groupEndMin, eventEndMin)

                    group.startTime = `${Math.floor(newStartMin / 60)}:${(newStartMin % 60).toString().padStart(2, '0')}`
                    group.endTime = `${Math.floor(newEndMin / 60)}:${(newEndMin % 60).toString().padStart(2, '0')}`

                    addedToGroup = true
                    break
                }
            }

            // Create new group if not added to existing
            if (!addedToGroup) {
                groups.push({
                    startTime: event.start_time,
                    endTime: event.end_time,
                    events: [event],
                })
            }
        }

        return groups
    }

    const events: EmployeeEvent[] = (eventsData?.results?.filter(
        (event: EmployeeEvent) => event.event_category === eventScope
    ) || []) as unknown as EmployeeEvent[]

    const eventGroups = groupEventsByTime(events)

    // Auto-scroll effect
    useEffect(() => {
        if (
            eventsData &&
            eventGroups.length > 0 &&
            scrollContainerRef.current
        ) {
            const targetGroupIndex = findClosestGroupToCurrentTime(eventGroups)
            const targetElement = groupRefs.current[targetGroupIndex]

            if (targetElement) {
                // Use setTimeout to ensure DOM is fully rendered
                setTimeout(() => {
                    targetElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    })
                }, 100)
            }
        }
    }, [eventScope, eventsData]) // Re-run when data changes or scope changes

    const stateComponent = renderState()
    if (stateComponent) {
        return (
            <>
                <ButtonGroupSkeleton length={2} />
                <EventSkeleton />
            </>
        )
    }

    return (
        <>
            <ButtonGroupCustomized
                className="p-2"
                active={eventScope}
                onChange={(value) =>
                    setEventScope(value as 'public' | 'personal')
                }
                buttons={[
                    {
                        label: t('events.all'),
                        value: 'public',
                        count: eventsData?.results?.filter(
                            (event: EmployeeEvent) =>
                                event.event_category === 'public'
                        ).length,
                    },
                    {
                        label: t('events.my_events'),
                        value: 'personal',
                        count: eventsData?.results?.filter(
                            (event: EmployeeEvent) =>
                                event.event_category === 'personal'
                        ).length,
                    },
                ]}
            />
            <div className="p-4">
                {eventGroups.length === 0 && <NoEvents />}
                <div
                    ref={scrollContainerRef}
                    className="flex-1 overflow-y-auto xs:max-h-[300px] md:max-h-[386.43px]"
                >
                    {eventGroups.map(
                        (group: EventGroup, groupIndex: number) => (
                            <div
                                key={`group-${groupIndex}`}
                                ref={(el) =>
                                    (groupRefs.current[groupIndex] = el)
                                }
                            >
                                <div className="grid grid-cols-12">
                                    <div
                                        className={`xs:col-span-4 md:col-span-3 ${groupIndex !== 0 ? 'pt-6' : 'pt-2'}`}
                                    >
                                        <Typogaphy
                                            size="small"
                                            className={`text-sm font-medium text-[#2A2A2A] ${isCurrentTimeInRange(group.startTime, group.endTime) ? '' : ''}`}
                                        >
                                            {group.startTime}-{group.endTime}
                                        </Typogaphy>
                                    </div>
                                    <div className="xs:col-span-8 md:col-span-9 border-s border-gray-300">
                                        <div
                                            className={`ps-4 pe-2 pb-2 ${groupIndex !== 0 ? 'pt-6' : 'pt-2'}`}
                                        >
                                            {group.events.map(
                                                (
                                                    event: EmployeeEvent,
                                                    eventIndex: number
                                                ) => (
                                                    <div
                                                        key={event.event_id}
                                                        className={`flex flex-col gap-1 relative ${eventIndex > 0 ? 'mt-3' : ''}`}
                                                    >
                                                        <div className="w-1.5 h-1.5 bg-primary rounded-full absolute top-1.5 left-[-19px]"></div>

                                                        <div className="flex items-center gap-2">
                                                            <Typogaphy
                                                                size="small"
                                                                className="text-primary font-medium"
                                                            >
                                                                {event?.title}
                                                            </Typogaphy>
                                                        </div>

                                                        {event?.description && (
                                                            <div>
                                                                <TextEditorCustomize
                                                                    readOnly
                                                                    maxLine={3}
                                                                    value={{
                                                                        text: event?.description,
                                                                        attachments:
                                                                            [],
                                                                    }}
                                                                    className="!p-0"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </>
    )
}

export default Community
